import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

// COMPONENTS
import Dropdown from 'react-dropdown';
import 'react-dropdown/style.css';

// DATA
import states from './states';

import styles from './Dropdown.module.css';
import {
  OPTION_ACTIVE_CLASS,
  flattenOptions,
  getOptionValue,
  getOptionLabel,
  findSelectedIndex
} from '../../shared/utilities/common';

const dropDownIcon = require('./imgs/dropdown-icon.png');

/**
 * Returns image element with dropdown icon asset
 */
const buildDropArrow = compactLayout => (
  <img
    className={[styles.dropDownIcon, compactLayout && styles.dropDownIconHidden].filter(Boolean).join(' ')}
    alt=""
    aria-hidden="true"
    src={dropDownIcon}
  />
);

let listboxIdCounter = 0;
const nextListboxId = () => {
  listboxIdCounter += 1;
  return `dropdown-listbox-${listboxIdCounter}`;
};

const DropdownComponent = ({
  disabled,
  value,
  onChange,
  theme,
  isDate,
  isTime,
  options,
  customDropDownStyles,
  className,
  ariaLabel,
  ariaDescribedBy,
  ariaInvalid,
  ariaRequired,
  compactLayout
}) => {
  const wrapperRef = useRef(null);
  const comboboxRef = useRef(null);
  const listboxIdRef = useRef(nextListboxId());
  const pendingNavRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const flatOptions = useMemo(() => flattenOptions(options), [options]);

  const toggleFromControl = useCallback(() => {
    const root = wrapperRef.current?.querySelector('.Dropdown-root');
    if (!root?.classList.contains('is-open')) return;
    const control = wrapperRef.current?.querySelector('.Dropdown-control');
    if (control) {
      control.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
    }
  }, []);

  const openMenu = useCallback(() => {
    const root = wrapperRef.current?.querySelector('.Dropdown-root');
    if (root?.classList.contains('is-open')) return;
    const control = wrapperRef.current?.querySelector('.Dropdown-control');
    if (control) {
      control.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
    }
  }, []);

  const selectOption = useCallback(
    option => {
      onChange({
        value: getOptionValue(option),
        label: getOptionLabel(option)
      });
      toggleFromControl();
    },
    [onChange, toggleFromControl]
  );

  useEffect(() => {
    const root = wrapperRef.current?.querySelector('.Dropdown-root');
    if (!root) return undefined;

    const syncOpen = () => {
      setIsOpen(root.classList.contains('is-open'));
    };
    syncOpen();

    const observer = new MutationObserver(syncOpen);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, [value, options]);

  useEffect(() => {
    if (!isOpen) {
      setActiveIndex(-1);
      pendingNavRef.current = null;
      return;
    }

    const selectedIndex = findSelectedIndex(flatOptions, value);
    if (pendingNavRef.current === 'down') {
      setActiveIndex(Math.min(selectedIndex + 1, flatOptions.length - 1));
    } else if (pendingNavRef.current === 'up') {
      setActiveIndex(Math.max(selectedIndex - 1, 0));
    } else {
      setActiveIndex(selectedIndex);
    }
    pendingNavRef.current = null;
  }, [isOpen, flatOptions, value]);

  useEffect(() => {
    const control = wrapperRef.current?.querySelector('.Dropdown-control');
    if (control) {
      control.tabIndex = -1;
      control.removeAttribute('aria-haspopup');
    }
  });

  useEffect(() => {
    if (!isOpen) return undefined;
    const menu = wrapperRef.current?.querySelector('.Dropdown-menu');
    if (!menu) return undefined;

    menu.setAttribute('id', listboxIdRef.current);
    menu.setAttribute('role', 'listbox');
    menu.removeAttribute('aria-expanded');

    return () => {
      menu.removeAttribute('id');
      menu.removeAttribute('role');
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || activeIndex < 0) return undefined;

    const applyActiveOptionStyles = () => {
      const menu = wrapperRef.current?.querySelector('.Dropdown-menu');
      if (!menu) return;

      const optionEls = menu.querySelectorAll('.Dropdown-option');
      const selectedIndex = findSelectedIndex(flatOptions, value);

      optionEls.forEach((el, index) => {
        el.setAttribute('id', `${listboxIdRef.current}-option-${index}`);
        el.setAttribute('role', 'option');

        const isSelected = index === selectedIndex;
        const isActive = index === activeIndex;
        el.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        el.classList.toggle(OPTION_ACTIVE_CLASS, isActive);

        if (isActive) {
          el.scrollIntoView({ block: 'nearest' });
        }
      });
    };

    applyActiveOptionStyles();

    const menu = wrapperRef.current?.querySelector('.Dropdown-menu');
    if (!menu) return undefined;

    const observer = new MutationObserver(applyActiveOptionStyles);
    observer.observe(menu, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      menu.querySelectorAll('.Dropdown-option').forEach(el => {
        el.classList.remove(OPTION_ACTIVE_CLASS);
        el.removeAttribute('id');
      });
    };
  }, [isOpen, activeIndex, flatOptions, value]);

  const handleKeyDown = e => {
    if (disabled || flatOptions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) {
        pendingNavRef.current = 'down';
        openMenu();
        return;
      }
      setActiveIndex(prev => Math.min(prev + 1, flatOptions.length - 1));
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!isOpen) {
        pendingNavRef.current = 'up';
        openMenu();
        return;
      }
      setActiveIndex(prev => Math.max(prev - 1, 0));
      return;
    }

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (isOpen && activeIndex >= 0) {
        selectOption(flatOptions[activeIndex]);
        return;
      }
      openMenu();
      return;
    }

    if (e.key === 'Escape' && isOpen) {
      e.preventDefault();
      toggleFromControl();
      return;
    }

    if (e.key === 'Tab' && isOpen) {
      toggleFromControl();
    }
  };

  const themeClassName = theme === 'Dark' ? styles.isDark : styles.isLight;
  const arrows = compactLayout ? buildDropArrow(true) : buildDropArrow(false);

  /** Same chrome as legacy SimpleDropdown (phone country strip, etc.). */
  const dropdownRootClassName = compactLayout
    ? `${className || ''} ${themeClassName}`.trim()
    : [
        className || '',
        'rounded-[40px] dark:placeholder-white placeholder-dark bg-transparent',
        styles.dropDownContainer,
        disabled ? styles.isDisabled : '',
        themeClassName,
        'dropdownThemeOverride'
      ]
        .filter(Boolean)
        .join(' ');

  const dropdownMenuClassName = compactLayout
    ? ''
    : `${styles.dropDownMenuContainer} dropDownOverride font-montserratMedium  text-dark dark:text-white
    w-full border-[1px]
    border-borderLight dark:border-none bg-transparent dark:bg-darkDry placeholder-dark dark:placeholder-white`;

  const dropdownPlaceholderClassName = compactLayout
    ? customDropDownStyles || ''
    : [
        styles.dropDownPlaceholder,
        isDate ? styles.dropDownPlaceholderDate : null,
        isTime ? styles.dropDownPlaceholderTime : null,
        customDropDownStyles,
        'dropDownPlaceholderOverride'
      ]
        .filter(Boolean)
        .join(' ');

  return (
    <div ref={wrapperRef}>
      <div
        ref={comboboxRef}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxIdRef.current}
        aria-activedescendant={
          !disabled && isOpen && activeIndex >= 0
            ? `${listboxIdRef.current}-option-${activeIndex}`
            : undefined
        }
        aria-autocomplete="none"
        aria-label={ariaLabel || undefined}
        aria-describedby={ariaDescribedBy || undefined}
        aria-invalid={ariaInvalid === true ? 'true' : undefined}
        aria-required={ariaRequired === true ? 'true' : undefined}
        aria-disabled={disabled ? 'true' : undefined}
        className={styles.comboboxFocusRoot}
      >
        <Dropdown
          onChange={onChange}
          value={value}
          disabled={disabled}
          arrowClosed={arrows}
          arrowOpen={arrows}
          menuClassName={dropdownMenuClassName}
          placeholderClassName={dropdownPlaceholderClassName}
          controlClassName={compactLayout ? '' : styles.dropDownControl}
          className={dropdownRootClassName}
          options={options}
        />
      </div>
    </div>
  );
};

DropdownComponent.propTypes = {
  value: PropTypes.any, // eslint-disable-line
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  customDropDownStyles: PropTypes.string, // eslint-disable-line
  theme: PropTypes.string,
  isDate: PropTypes.bool,
  isTime: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.any
    })
  ),
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
  ariaDescribedBy: PropTypes.string,
  ariaInvalid: PropTypes.bool,
  ariaRequired: PropTypes.bool,
  compactLayout: PropTypes.bool
};

DropdownComponent.defaultProps = {
  disabled: false,
  theme: 'Dark',
  isDate: false,
  isTime: false,
  options: states,
  className: null,
  ariaLabel: undefined,
  ariaDescribedBy: undefined,
  ariaInvalid: undefined,
  ariaRequired: undefined,
  compactLayout: false
};

export default DropdownComponent;
